import React from "react";
import { getStraightPath } from "reactflow";
import { IconButton, Box, useTheme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

const PlusEdge = ({ id, sourceX, sourceY, targetX, targetY, style = {}, markerEnd, onPlusClick, data }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  
  // Determine if this is a horizontal branching edge
  const isHorizontalBranch = data?.isHorizontalBranch || Math.abs(targetX - sourceX) > Math.abs(targetY - sourceY);
  
  // Check if this is a "No reply" connection
  const isNoReplyConnection = data?.branchId?.startsWith('no-reply-');
  
  let edgePath, labelX, labelY;
  
  if (isHorizontalBranch) {
    // For horizontal branching, create a path that goes down first, then horizontally, then down
    const midY1 = sourceY + 50; // Go down 50px first
    const midY2 = targetY - 50; // Go up 50px at the end
    const midX = targetX; // Then go horizontally to target
    
    // Create a custom path: source -> down -> horizontal -> up -> target
    edgePath = `M ${sourceX} ${sourceY} L ${sourceX} ${midY1} L ${midX} ${midY1} L ${midX} ${midY2} L ${targetX} ${targetY}`;
    labelX = (sourceX + targetX) / 2;
    labelY = midY1;
  } else {
    // Use straight path for vertical connections
    [edgePath, labelX, labelY] = getStraightPath({ 
      sourceX, 
      sourceY, 
      targetX, 
      targetY 
    });
  }
  
  const handlePlusClick = (event) => {
    event.stopPropagation();
    if (onPlusClick) onPlusClick(id);
  };
  
  return (
    <g>
      <path 
        id={id} 
        style={{
          ...style,
          strokeWidth: isHorizontalBranch ? 2 : 1,
          strokeDasharray: isNoReplyConnection ? "8,4" : (isHorizontalBranch ? "5,5" : "none"), // Dashed for No reply and horizontal branching
          stroke: isNoReplyConnection ? "#999" : style.stroke, // Different color for No reply
        }} 
        className="react-flow__edge-path" 
        d={edgePath} 
        markerEnd={markerEnd} 
      />
      {/* Only show plus button on vertical edges (not horizontal branching) */}
      {!isHorizontalBranch && (
        <foreignObject 
          x={labelX - 16} 
          y={labelY - 16} 
          width={32} 
          height={32} 
          style={{ 
            overflow: 'visible', 
            zIndex: 10000,
            pointerEvents: 'all'
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: 32, 
            width: 32, 
            position: 'relative', 
            zIndex: 10000,
            pointerEvents: 'all'
          }}>
            <IconButton
              size="small"
              onClick={handlePlusClick}
              sx={{
                bgcolor: isDark ? '#23272b' : '#fff',
                border: `2px solid ${isDark ? '#444' : '#b0b0b0'}`,
                boxShadow: 4,
                p: 0.5,
                '&:hover': { 
                  bgcolor: isDark ? '#2e4d44' : '#e0f7fa',
                  boxShadow: 6,
                  transform: 'scale(1.1)'
                },
                color: isDark ? theme.palette.primary.light : theme.palette.primary.main,
                zIndex: 10000,
                position: 'relative',
                pointerEvents: 'all',
              }}
            >
              <AddIcon fontSize="small" sx={{ color: isDark ? theme.palette.primary.light : theme.palette.primary.main }} />
            </IconButton>
          </Box>
        </foreignObject>
      )}
    </g>
  );
};

export default PlusEdge; 